import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Printer, FileText, Loader2, ChevronRight } from "lucide-react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { auth, db, messaging } from "../../firebase";
import { collection, addDoc, query, where, getDocs, serverTimestamp } from "firebase/firestore";
import { getToken } from "firebase/messaging";
import InfoModal from "../../components/InfoModal";
import { useAuth } from "../../context/AuthContext";
import featureGatingService from "../../services/featureGatingService";
import CaseLimitDialog from "../../components/CaseLimitDialog";
import toast from "react-hot-toast";
import { dcPost, DC_API_BASE_URL } from "../../services/districtCourtApi";
import { statusPillSolid } from "../../styles/statusPill";

function DistrictCourtCaseDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { caseDetails, sessionId, complexCode, searchBy } = location.state || {};
  const [exporting, setExporting] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [eventDates, setEventDates] = useState({ reminderDate: null, mainEventDate: null });
  const [userMobile, setUserMobile] = useState("");
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateCaseInfo, setDuplicateCaseInfo] = useState(null);

  // Business detail modal state
  const [businessModalOpen, setBusinessModalOpen] = useState(false);
  const [businessDetail, setBusinessDetail] = useState(null);
  const [businessLoading, setBusinessLoading] = useState(false);

  // Order PDF loading state
  const [orderLoading, setOrderLoading] = useState(null);

  const { globalUser } = useAuth();
  const userId = globalUser?.uid;

  // Case limit states
  const [showCaseLimitDialog, setShowCaseLimitDialog] = useState(false);
  const [caseLimitData, setCaseLimitData] = useState({
    currentCount: 0,
    maxLimit: 0,
    planName: ''
  });

  // Get payload data from localStorage
  const [combinedPayload, setCombinedPayload] = useState(null);

  useEffect(() => {
    const searchPayload = localStorage.getItem('dc_search_payload');
    const detailsPayload = localStorage.getItem('dc_case_details_payload');
    const districtsPayload = localStorage.getItem('dc_districts_payload');

    if (searchPayload || detailsPayload || districtsPayload) {
      const combined = {
        searchPayload: searchPayload ? JSON.parse(searchPayload) : null,
        districtsPayload: districtsPayload ? JSON.parse(districtsPayload) : null,
        detailsPayload: detailsPayload ? JSON.parse(detailsPayload) : null,
        retrievedAt: new Date().toISOString(),
      };
      setCombinedPayload(combined);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      try {
        const userQuery = query(
          collection(db, "lawyers"),
          where("uid", "==", userId)
        );
        const userDocSnapshot = await getDocs(userQuery);
        if (!userDocSnapshot.empty) {
          const userData = userDocSnapshot.docs[0].data();
          setUserMobile(userData?.mobileWithCountryCode || "");
        } else {
          console.warn("User document not found for UID:", userId);
          setUserMobile("");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, [userId]);

  // Format dates to "DD/MM/YYYY"
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const monthNames = {
      january: "01", february: "02", march: "03", april: "04",
      may: "05", june: "06", july: "07", august: "08",
      september: "09", october: "10", november: "11", december: "12",
    };
    // "dd-mm-yyyy"
    const hyphenMatch = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (hyphenMatch) {
      return `${hyphenMatch[1]}/${hyphenMatch[2]}/${hyphenMatch[3]}`;
    }
    // "dd-Month-yyyy"
    const dashedMatch = dateStr.match(
      /^(\d{1,2})-(January|February|March|April|May|June|July|August|September|October|November|December)-(\d{4})$/i
    );
    if (dashedMatch) {
      const day = dashedMatch[1].padStart(2, "0");
      const month = monthNames[dashedMatch[2].toLowerCase()];
      return `${day}/${month}/${dashedMatch[3]}`;
    }
    // "2nd April 2026", "13th March 2024", "25th January 2024"
    const naturalMatch = dateStr.match(
      /^(\d{1,2})(?:st|nd|rd|th)?\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})$/i
    );
    if (naturalMatch) {
      const day = naturalMatch[1].padStart(2, "0");
      const month = monthNames[naturalMatch[2].toLowerCase()];
      return `${day}/${month}/${naturalMatch[3]}`;
    }
    return dateStr;
  };

  const extractPetitionerAdvocates = (petitionersArray) => {
    const advocates = new Set();
    if (Array.isArray(petitionersArray)) {
      petitionersArray.forEach(petitioner => {
        if (petitioner.advocate && petitioner.advocate.trim() !== '') {
          advocates.add(petitioner.advocate.trim());
        }
      });
    }
    return Array.from(advocates);
  };

  const extractRespondentAdvocates = (respondentsArray) => {
    const advocates = new Set();
    if (Array.isArray(respondentsArray)) {
      respondentsArray.forEach(respondent => {
        if (respondent.advocate && respondent.advocate.trim() !== '') {
          advocates.add(respondent.advocate.trim());
        }
      });
    }
    return Array.from(advocates);
  };

  // Fetch business detail for a case history entry
  const handleBusinessClick = useCallback(async (historyEntry) => {
    if (!sessionId || !historyEntry.business_params) {
      toast.error("Business details not available");
      return;
    }

    setBusinessLoading(true);
    setBusinessModalOpen(true);
    setBusinessDetail(null);

    try {
      const bp = historyEntry.business_params;
      const payload = {
        sessionId,
        cino: bp.case_number1,
        court_code: bp.court_code,
        state_code: bp.state_code,
        dist_code: bp.dist_code,
        court_complex_code: complexCode || "",
        nextdate1: bp.nextdate1,
        case_number1: bp.case_number1,
        disposal_flag: bp.disposal_flag,
        businessDate: bp.businessDate,
        national_court_code: bp.national_court_code,
        court_no: bp.court_no,
        search_by: bp.search_by,
        srno: bp.srno,
      };

      const businessEndpoint = searchBy === "CScaseNumber"
        ? "/api/casenumber/business-detail"
        : "/api/partyname/business-detail";

      const response = await dcPost(businessEndpoint, payload);
      const data = response.body;

      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Failed to fetch business details");
      }

      setBusinessDetail(data.result || data);
    } catch (err) {
      console.error("Business detail error:", err);
      toast.error(err.message || "Failed to fetch business details");
      setBusinessModalOpen(false);
    } finally {
      setBusinessLoading(false);
    }
  }, [sessionId, complexCode, searchBy]);

  // Handle order PDF click — re-fetches case-detail on a fresh session to get valid pdfProxy
  const handleOrderPdfClick = useCallback(async (order, index) => {
    if (!order.pdfProxy) {
      toast.error("PDF not available for this order");
      return;
    }

    setOrderLoading(index);
    try {
      // 1. Create a fresh session
      const initRes = await dcPost("/api/common/init", {});
      const initData = initRes.body;
      const freshSid = initData?.result?.sessionId || initData?.sessionId || "";
      if (!freshSid) throw new Error("Failed to create session for PDF");

      // 2. Set fields on the fresh session
      const detailsPayload = JSON.parse(localStorage.getItem("dc_case_details_payload") || "{}");
      await dcPost("/api/common/set-fields", {
        sessionId: freshSid,
        stateCode: detailsPayload.stateCode || "",
        distCode: detailsPayload.distCode || "",
        complexCode: detailsPayload.complexCode || complexCode || "",
      });

      // 3. Re-fetch case-detail to get fresh encrypted pdfProxy URLs
      const caseDetailEndpoint = searchBy === "CScaseNumber"
        ? "/api/casenumber/case-detail"
        : "/api/partyname/case-detail";
      const caseRes = await dcPost(caseDetailEndpoint, { ...detailsPayload, sessionId: freshSid });
      const caseData = caseRes.body;
      const result = caseData?.result || {};

      // 4. Find the matching order by order_number + order_date
      const allOrders = [...(result.interim_orders || []), ...(result.final_orders || [])];
      const freshOrder = allOrders.find(
        (o) => o.order_number === order.order_number && o.order_date === order.order_date
      );

      if (!freshOrder?.pdfProxy) {
        throw new Error("Could not get fresh PDF link for this order");
      }

      // 5. Open PDF with fresh pdfProxy (already has freshSid baked in).
      // PDF stream is binary — leave the request plain since the backend
      // middleware skips encryption for non-JSON responses anyway.
      const pdfUrl = `${DC_API_BASE_URL}${freshOrder.pdfProxy}`;
      window.open(pdfUrl, "_blank");
    } catch (err) {
      console.error("Order PDF error:", err);
      toast.error(err.message || "Failed to open order PDF");
    } finally {
      setOrderLoading(null);
    }
  }, [complexCode, searchBy]);

  // Print business detail
  const handlePrintBusiness = useCallback(() => {
    if (!businessDetail) return;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head><title>Business Detail</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background: #f0f0f0; }
          </style>
        </head>
        <body>
          <h2>Business Detail</h2>
          <table>
            <tr><th>Court</th><td>${businessDetail.court_of || "N/A"}</td></tr>
            <tr><th>CNR Number</th><td>${businessDetail.cnr_number || "N/A"}</td></tr>
            <tr><th>Case Number</th><td>${businessDetail.case_number || "N/A"}</td></tr>
            <tr><th>Date</th><td>${businessDetail.date || "N/A"}</td></tr>
            <tr><th>Parties</th><td>${businessDetail.parties || "N/A"}</td></tr>
            <tr><th>Business</th><td>${businessDetail.business || "N/A"}</td></tr>
            <tr><th>Next Purpose</th><td>${businessDetail.next_purpose || "N/A"}</td></tr>
            <tr><th>Next Hearing Date</th><td>${formatDate(businessDetail.next_hearing_date)}</td></tr>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  }, [businessDetail]);

  const handleExportCase = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert("User not authenticated");
        return;
      }

      if (!caseDetails) {
        alert("No case details available to export");
        return;
      }

      const targetCollection =
        (caseDetails.case_status || "").toLowerCase().includes("pending") ? "pending" : "disposed";
      const result = await featureGatingService.checkCaseLimitBeforeExport(
        globalUser.uid,
        1,
        targetCollection
      );

      if (!result.canProceed) {
        setCaseLimitData({
          currentCount: result.currentCount,
          maxLimit: result.maxLimit,
          planName: result.planName
        });
        setShowCaseLimitDialog(true);
        return;
      }

      setExporting(true);

      const caseNo = caseDetails.registration_number || "";

      if (caseNo) {
        const pendingQuery = query(
          collection(db, "pending"),
          where("caseNo", "==", caseNo),
          where("owner", "==", user.uid)
        );
        const disposedQuery = query(
          collection(db, "disposed"),
          where("caseNo", "==", caseNo),
          where("owner", "==", user.uid)
        );
        const [pendingSnapshot, disposedSnapshot] = await Promise.all([
          getDocs(pendingQuery),
          getDocs(disposedQuery)
        ]);

        if (!pendingSnapshot.empty || !disposedSnapshot.empty) {
          const existingCollection = !pendingSnapshot.empty ? "pending" : "disposed";
          const existingDoc = !pendingSnapshot.empty ? pendingSnapshot.docs[0] : disposedSnapshot.docs[0];
          setDuplicateCaseInfo({
            caseNo: caseNo,
            collection: existingCollection,
            docId: existingDoc.id,
            data: existingDoc.data()
          });
          setDuplicateModalOpen(true);
          setExporting(false);
          return;
        }
      }

      const courtName = caseDetails.court_number_and_judge || "District Court";
      const caseStatus =
        (caseDetails.case_status || "").toLowerCase().includes("pending") ? "pending" : "disposed";
      const caseFiledDate = formatDate(caseDetails.filing_date || "");
      const nextHearingDate = formatDate(caseDetails.next_hearing_date || "");

      const caseHistoryRaw = caseDetails.history_of_case_hearing || caseDetails.case_history || [];
      const previousHearingDate = formatDate(
        caseHistoryRaw[caseHistoryRaw.length - 1]?.hearing_date || ""
      );
      const petitionerName = caseDetails.petitioners_and_advocates?.[0]?.name || caseDetails.petitioners?.[0]?.name || "N/A";
      const respondentName = caseDetails.respondents_and_advocates?.[0]?.name || caseDetails.respondents?.[0]?.name || "N/A";
      const caseHistoryForExport =
        caseHistoryRaw.map((history) => ({
          ...history,
          business_on_date: formatDate(history.business_on_date),
          hearing_date: formatDate(history.hearing_date),
        }));

      const petitionersArray = caseDetails.petitioners_and_advocates || caseDetails.petitioners || [];
      const respondentsArray = caseDetails.respondents_and_advocates || caseDetails.respondents || [];
      const allPetitionerAdvocates = extractPetitionerAdvocates(petitionersArray);
      const allRespondentAdvocates = extractRespondentAdvocates(respondentsArray);

      let currentPendingCount = 0;
      let subscriptionMetadata = null;

      if (caseStatus === "pending") {
        const pendingQuery = query(
          collection(db, "pending"),
          where("owner", "==", user.uid)
        );
        const pendingSnapshot = await getDocs(pendingQuery);
        currentPendingCount = pendingSnapshot.size;

        subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
          user.uid,
          currentPendingCount
        );
      }

      const caseData = {
        caseNo: caseDetails.registration_number || "",
        filingNumber: caseDetails.filing_number || "",
        caseType: caseDetails.case_type || "",
        caseFiledDate,
        nextHearingDate,
        previousHearingDate,
        courtName,
        petitionerName,
        petitionerAdvocate: petitionersArray[0]?.advocate || "",
        respondentName,
        respondentAdvocate: respondentsArray[0]?.advocate || "",
        allPetitionerAdvocates,
        allRespondentAdvocates,
        partyContactNo: "",
        appearingFor: "",
        team: [userId],
        files: [],
        casenotes: "",
        status: caseStatus,
        owner: user.uid,
        firDetails: Array.isArray(caseDetails.fir_details) && caseDetails.fir_details.length > 0
          ? {
            policeStation: caseDetails.fir_details[0]?.police_station || "",
            firNumber: caseDetails.fir_details[0]?.fir_number || "",
            year: caseDetails.fir_details[0]?.year || "",
          }
          : {
            policeStation: caseDetails.fir_details?.police_station || "",
            firNumber: caseDetails.fir_details?.fir_number || "",
            year: caseDetails.fir_details?.year || "",
          },
        acts: caseDetails.acts_and_sections?.length > 0
          ? {
            underAct: caseDetails.acts_and_sections[0]?.under_act_s || "",
            underSection: caseDetails.acts_and_sections[0]?.under_section_s || "",
          }
          : {
            underAct: caseDetails.acts?.under_act_s || "",
            underSection: caseDetails.acts?.under_section_s || "",
          },
        allPetitioners: petitionersArray,
        allRespondents: respondentsArray,
        allActs: caseDetails.acts_and_sections || [],
        iaStatus: caseDetails.ia_status || [],
        caseTransferDetails: caseDetails.case_transfer_details || [],
        orders: caseDetails.orders || [],
        processDetails: caseDetails.process_details || [],
        caseHistory: caseHistoryForExport,
        params: (() => {
          const dp = combinedPayload?.detailsPayload || {};
          const { sessionId, ...rest } = dp;
          return rest;
        })(),
        payload: combinedPayload ? {
          ...combinedPayload,
          caseexportedfrom: "DistrictCourt",
        } : {
          exportedAt: new Date().toISOString(),
          caseexportedfrom: "DistrictCourt",
        },
        ...(subscriptionMetadata && { subscriptionMetadata }),
        timestamp: serverTimestamp(),
      };

      const caseRef = await addDoc(collection(db, caseStatus), caseData);
      console.log("Case added to Firestore:", caseRef.id);

      try {
        await getToken(messaging, {
          vapidKey:
            "BOSi0E-WalUbqOuB5su9XW2J39ot0D4x-fSPRYo_AxMVloqe7jIkpYXf5J007lXs1D_ZZB9YH1Hc0X-7jQrqR38",
        });
      } catch (err) {
        console.warn("Could not get FCM token:", err);
      }

      if (nextHearingDate) {
        try {
          const eventDateStr = nextHearingDate.split("/").reverse().join("-");
          const eventDate = new Date(eventDateStr);
          if (isNaN(eventDate.getTime())) {
            console.warn("Invalid hearing date format:", nextHearingDate);
          } else {
            const reminderDate = new Date(eventDate);
            reminderDate.setHours(8, 0, 0, 0);

            const eventTimeIST = new Date(eventDate);
            eventTimeIST.setHours(18, 0, 0, 0);

            const now = new Date();

            setEventDates({
              reminderDate: reminderDate > now ? reminderDate : null,
              mainEventDate: eventTimeIST > now ? eventTimeIST : null
            });

            if (reminderDate > now) {
              const reminderEventData = {
                "Case No": caseDetails.registration_number || "",
                date: reminderDate.toISOString().split("T")[0],
                id: Math.floor(Math.random() * 90000) + 10000,
                mobile: userMobile || null,
                time: "08:00 AM",
                time1: reminderDate.getTime(),
                title: `${petitionerName} VS ${respondentName}`,
                description: `Hearing Reminder : Case No. - ${caseDetails.registration_number || ""}`,
                isScheduled: reminderDate > now,
                status: caseStatus,
                eventType: "reminder",
              };
              const reminderEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), reminderEventData);

              const reminderDoc = {
                eventId: reminderEventRef.id,
                reminderTime: reminderDate,
                eventTitle: `Hearing Reminder : Case No. - ${caseDetails.registration_number || ""}`,
                caseNo: caseDetails.registration_number || "",
                status: "pending",
                createdAt: new Date(),
                scheduledBy: user.uid,
                recipientId: user.uid,
              };
              await addDoc(collection(db, "eventReminders"), reminderDoc);
            }

            if (eventTimeIST > now) {
              const mainEventData = {
                "Case No": caseDetails.registration_number || "",
                date: eventDate.toISOString().split("T")[0],
                id: Math.floor(Math.random() * 90000) + 10000,
                mobile: userMobile || null,
                time: "06:00 PM",
                time1: eventTimeIST.getTime(),
                title: `Update Next Hearing: ${petitionerName} VS ${respondentName}`,
                description: `Case No. - ${caseDetails.registration_number || ""}`,
                isScheduled: eventTimeIST > now,
                status: caseStatus,
                eventType: "updateReminder",
                isHidden: true,
              };
              const mainEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), mainEventData);

              const mainReminderDoc = {
                eventId: mainEventRef.id,
                reminderTime: eventTimeIST,
                eventTitle: `Update Next Hearing : ${caseDetails.registration_number || ""}`,
                caseNo: caseDetails.registration_number || "",
                status: "pending",
                createdAt: new Date(),
                scheduledBy: user.uid,
                recipientId: user.uid,
              };
              await addDoc(collection(db, "eventReminders"), mainReminderDoc);
            }
          }
        } catch (notificationError) {
          console.error("Notification setup error:", notificationError);
        }
      }

      setIsInfoModalOpen(true);
    } catch (error) {
      console.error("Export error:", error);
      alert(`Error exporting case: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  if (!caseDetails) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="text-slate-500 text-center mt-10">
          No case details available
        </div>
      </div>
    );
  }


  const petitionerName = caseDetails.petitioners_and_advocates?.[0]?.name || caseDetails.petitioners?.[0]?.name || "N/A";
  const respondentName = caseDetails.respondents_and_advocates?.[0]?.name || caseDetails.respondents?.[0]?.name || "N/A";
  const caseHistory = caseDetails.history_of_case_hearing || caseDetails.case_history || [];
  const interimOrders = caseDetails.interim_orders || [];
  const finalOrders = caseDetails.final_orders || [];
  const connectedCases = caseDetails.connected_cases || [];

  // Determine display status
  const displayStatus = caseDetails.case_status || caseDetails.stage_of_case || "Unknown";

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800">
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="mb-4 flex items-center gap-1.5 text-sm">
          <button onClick={() => navigate("/portal/findCasesOptions")} className="text-slate-500 hover:text-slate-800">Find Cases</button>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-800">Results</button>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Import Case</span>
        </div>
        <div className="mb-4"><FindCaseStepper current={4} /></div>
        <div className="mb-4">
          <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Review Case Information</h1>
          <p className="mt-0.5 text-xs text-slate-500">Review the details before importing this case.</p>
        </div>

        <div className="space-y-4">
          {/* Header Section */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            {caseDetails.court_name && (
              <p className="text-sm text-slate-500 mb-1">{caseDetails.court_name}</p>
            )}
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">{petitionerName} vs {respondentName}</h2>
              <span
                className={`px-3 py-1 rounded-full text-sm text-white ${statusPillSolid(displayStatus)}`}
              >
                {displayStatus}
              </span>
            </div>
          </div>

          {/* Case Information */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-lg font-bold mb-2">Case Information</h3>
            <hr className="border-slate-200 mb-4" />
            <InfoRow label="Case Type" value={caseDetails.case_type} />
            <InfoRow label="Filing Number" value={caseDetails.filing_number} />
            <InfoRow label="Filing Date" value={formatDate(caseDetails.filing_date)} />
            <InfoRow label="Registration Number" value={caseDetails.registration_number} />
            <InfoRow label="Registration Date" value={formatDate(caseDetails.registration_date)} />
            <InfoRow label="CNR Number" value={caseDetails.cnr_number} />
          </div>

          {/* Case Status */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-lg font-bold mb-2">Case Status</h3>
            <hr className="border-slate-200 mb-4" />
            <InfoRow label="First Hearing Date" value={formatDate(caseDetails.first_hearing_date)} />
            {caseDetails.next_hearing_date && (
              <InfoRow label="Next Hearing Date" value={formatDate(caseDetails.next_hearing_date)} />
            )}
            {caseDetails.decision_date && (
              <InfoRow label="Decision Date" value={formatDate(caseDetails.decision_date)} />
            )}
            {caseDetails.case_status && (
              <InfoRow label="Case Status" value={caseDetails.case_status} />
            )}
            {caseDetails.nature_of_disposal && (
              <InfoRow label="Nature of Disposal" value={caseDetails.nature_of_disposal} />
            )}
            {caseDetails.stage_of_case && (
              <InfoRow label="Stage of Case" value={caseDetails.stage_of_case} />
            )}
            <InfoRow label="Court" value={caseDetails.court_number_and_judge} />
          </div>

          {/* Petitioner and Advocate */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-lg font-bold mb-2">Petitioner and Advocate</h3>
            <hr className="border-slate-200 mb-4" />
            {(caseDetails.petitioners_and_advocates || caseDetails.petitioners)?.length > 0 ? (
              (caseDetails.petitioners_and_advocates || caseDetails.petitioners).map((petitioner, index) => (
                <div key={index} className="mb-3 pl-4 border-l-2 border-slate-200">
                  <InfoRow label={`Petitioner ${index + 1}`} value={petitioner.name || "N/A"} />
                  {petitioner.advocate && <InfoRow label="Advocate" value={petitioner.advocate} />}
                </div>
              ))
            ) : (
              <InfoRow label="Petitioner" value="N/A" />
            )}
          </div>

          {/* Respondent and Advocate */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-lg font-bold mb-2">Respondent and Advocate</h3>
            <hr className="border-slate-200 mb-4" />
            {(caseDetails.respondents_and_advocates || caseDetails.respondents)?.length > 0 ? (
              (caseDetails.respondents_and_advocates || caseDetails.respondents).map((respondent, index) => (
                <div key={index} className="mb-3 pl-4 border-l-2 border-slate-200">
                  <InfoRow label={`Respondent ${index + 1}`} value={respondent.name || "N/A"} />
                  {respondent.advocate && <InfoRow label="Advocate" value={respondent.advocate} />}
                </div>
              ))
            ) : (
              <InfoRow label="Respondent" value="N/A" />
            )}
          </div>

          {/* Acts */}
          {caseDetails.acts_and_sections && caseDetails.acts_and_sections.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-bold mb-2">Acts</h3>
              <hr className="border-slate-200 mb-4" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-2">Under Act(s)</th>
                      <th className="px-4 py-2">Under Section(s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caseDetails.acts_and_sections.map((act, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="px-4 py-2">{act.under_act_s || "N/A"}</td>
                        <td className="px-4 py-2">{act.under_section_s || "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* FIR Details */}
          {caseDetails.fir_details && (
            Array.isArray(caseDetails.fir_details) ? caseDetails.fir_details.length > 0 : caseDetails.fir_details.police_station
          ) && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-bold mb-2">FIR Details</h3>
              <hr className="border-slate-200 mb-4" />
              {Array.isArray(caseDetails.fir_details) ? (
                caseDetails.fir_details.map((fir, index) => (
                  <div key={index} className="mb-3 pl-4 border-l-2 border-slate-200">
                    <InfoRow label="Police Station" value={fir.police_station || "N/A"} />
                    <InfoRow label="FIR Number" value={fir.fir_number || "N/A"} />
                    <InfoRow label="Year" value={fir.year || "N/A"} />
                  </div>
                ))
              ) : (
                <>
                  <InfoRow label="Police Station" value={caseDetails.fir_details?.police_station || "N/A"} />
                  <InfoRow label="FIR Number" value={caseDetails.fir_details?.fir_number || "N/A"} />
                  <InfoRow label="Year" value={caseDetails.fir_details?.year || "N/A"} />
                </>
              )}
            </div>
          )}

          {/* IA Status */}
          {caseDetails.ia_status && caseDetails.ia_status.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-bold mb-2">IA Status (Interlocutory Applications)</h3>
              <hr className="border-slate-200 mb-4" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-2">IA Number</th>
                      <th className="px-4 py-2">Party Name</th>
                      <th className="px-4 py-2">Date of Filing</th>
                      <th className="px-4 py-2">Next Date</th>
                      <th className="px-4 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caseDetails.ia_status.map((ia, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="px-4 py-2">{ia.ia_number || "N/A"}</td>
                        <td className="px-4 py-2">{ia.party_name || "N/A"}</td>
                        <td className="px-4 py-2">{formatDate(ia.date_of_filing)}</td>
                        <td className="px-4 py-2">{formatDate(ia.next_date)}</td>
                        <td className="px-4 py-2">{ia.ia_status || "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Case History - with clickable Business on Date */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-lg font-bold mb-2">Case History</h3>
            <hr className="border-slate-200 mb-4" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-slate-600">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Judge</th>
                    <th className="px-4 py-2">Business On Date</th>
                    <th className="px-4 py-2">Hearing Date</th>
                    <th className="px-4 py-2">Purpose of Hearing</th>
                  </tr>
                </thead>
                <tbody>
                  {caseHistory.length > 0 ? (
                    caseHistory.map((history, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="px-4 py-2">{history.judge || "N/A"}</td>
                        <td className="px-4 py-2">
                          {history.business_params ? (
                            <button
                              onClick={() => handleBusinessClick(history)}
                              className="text-yellow-400 hover:text-yellow-300 underline cursor-pointer"
                            >
                              {formatDate(history.business_on_date)}
                            </button>
                          ) : (
                            formatDate(history.business_on_date)
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {history.hearing_date ? formatDate(history.hearing_date) : "N/A"}
                        </td>
                        <td className="px-4 py-2">
                          {history.purpose_of_hearing || "N/A"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-4 py-2 text-center">
                        No case history available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Interim Orders */}
          {interimOrders.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-bold mb-2">Interim Orders</h3>
              <hr className="border-slate-200 mb-4" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-2">Order Number</th>
                      <th className="px-4 py-2">Order Date</th>
                      <th className="px-4 py-2">Order Details</th>
                      <th className="px-4 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interimOrders.map((order, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="px-4 py-2">{order.order_number || "N/A"}</td>
                        <td className="px-4 py-2">{formatDate(order.order_date)}</td>
                        <td className="px-4 py-2">{order.order_details || "N/A"}</td>
                        <td className="px-4 py-2">
                          {order.pdfProxy ? (
                            <button
                              onClick={() => handleOrderPdfClick(order, `interim-${index}`)}
                              disabled={orderLoading === `interim-${index}`}
                              className="inline-flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors disabled:opacity-60"
                            >
                              {orderLoading === `interim-${index}` ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <FileText className="w-4 h-4" />
                              )}
                              View
                            </button>
                          ) : (
                            <span className="text-gray-500">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Final Orders / Judgements */}
          {finalOrders.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-bold mb-2">Final Orders / Judgements</h3>
              <hr className="border-slate-200 mb-4" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-2">Order Number</th>
                      <th className="px-4 py-2">Order Date</th>
                      <th className="px-4 py-2">Order Details</th>
                      <th className="px-4 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finalOrders.map((order, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="px-4 py-2">{order.order_number || "N/A"}</td>
                        <td className="px-4 py-2">{formatDate(order.order_date)}</td>
                        <td className="px-4 py-2">{order.order_details || "N/A"}</td>
                        <td className="px-4 py-2">
                          {order.pdfProxy ? (
                            <button
                              onClick={() => handleOrderPdfClick(order, `final-${index}`)}
                              disabled={orderLoading === `final-${index}`}
                              className="inline-flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors disabled:opacity-60"
                            >
                              {orderLoading === `final-${index}` ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <FileText className="w-4 h-4" />
                              )}
                              View
                            </button>
                          ) : (
                            <span className="text-gray-500">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Case Transfer Details */}
          {caseDetails.case_transfer_details && caseDetails.case_transfer_details.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-bold mb-2">Case Transfer Details</h3>
              <hr className="border-slate-200 mb-4" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-2">Registration Number</th>
                      <th className="px-4 py-2">Transfer Date</th>
                      <th className="px-4 py-2">From Court</th>
                      <th className="px-4 py-2">To Court</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caseDetails.case_transfer_details.map((transfer, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="px-4 py-2">{transfer.registration_number || "N/A"}</td>
                        <td className="px-4 py-2">{formatDate(transfer.transfer_date)}</td>
                        <td className="px-4 py-2">{transfer.from_court_number_and_judge || "N/A"}</td>
                        <td className="px-4 py-2">{transfer.to_court_number_and_judge || "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Connected Cases */}
          {connectedCases.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-bold mb-2">Connected Cases</h3>
              <hr className="border-slate-200 mb-4" />
              {connectedCases.map((cc, index) => (
                <div key={index} className="mb-2 pl-4 border-l-2 border-slate-200">
                  <p className="text-slate-600">{typeof cc === 'string' ? cc : JSON.stringify(cc)}</p>
                </div>
              ))}
            </div>
          )}

          {/* Import Button */}
          <div className="flex justify-end">
            <button
              onClick={handleExportCase}
              disabled={exporting}
              className={`bg-yellow-500 text-gray-900 py-2.5 px-8 rounded-lg flex items-center justify-center font-semibold text-sm shadow-sm hover:bg-yellow-600 transition-colors ${exporting ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {exporting ? "Importing..." : "Import Case"}
            </button>
          </div>
        </div>

      {/* Business Detail Modal */}
      {businessModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-bold text-slate-900">Business Detail</h3>
              <div className="flex items-center gap-2">
                {businessDetail && (
                  <button
                    onClick={handlePrintBusiness}
                    className="text-slate-500 hover:text-slate-700 p-1"
                    title="Print"
                  >
                    <Printer className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => setBusinessModalOpen(false)}
                  className="text-slate-500 hover:text-slate-700 p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4">
              {businessLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
                  <span className="ml-3 text-slate-500">Loading business details...</span>
                </div>
              ) : businessDetail ? (
                <div className="space-y-3">
                  <InfoRow label="Court" value={businessDetail.court_of} />
                  <InfoRow label="CNR Number" value={businessDetail.cnr_number} />
                  <InfoRow label="Case Number" value={businessDetail.case_number} />
                  <InfoRow label="Date" value={formatDate(businessDetail.date)} />
                  <InfoRow label="Parties" value={businessDetail.parties} />
                  <InfoRow label="Business" value={businessDetail.business} />
                  <InfoRow label="Next Purpose" value={businessDetail.next_purpose} />
                  <InfoRow label="Next Hearing Date" value={formatDate(businessDetail.next_hearing_date)} />
                </div>
              ) : (
                <p className="text-center text-slate-500 py-4">No business details available</p>
              )}
            </div>
          </div>
        </div>
      )}

      <InfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        title="Information"
        message={`Case exported successfully! ${eventDates.reminderDate || eventDates.mainEventDate ? 'Events have been scheduled:' : 'No future events were created (next hearing date is in the past).'}\n${eventDates.reminderDate ? `\n• Reminder at 8:00 AM (one day before next hearing date: ${eventDates.reminderDate.toLocaleDateString('en-GB')})` : ''}\n${eventDates.mainEventDate ? `• Update reminder at 6:00 PM (on next hearing date: ${eventDates.mainEventDate.toLocaleDateString('en-GB')})` : ''}`}
      />
      <InfoModal
        isOpen={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        title="Duplicate Case Found"
        message={`Case ${duplicateCaseInfo?.caseNo} is already exported and saved in your profile under ${duplicateCaseInfo?.collection} cases.`}
      />

      <CaseLimitDialog
        isOpen={showCaseLimitDialog}
        onClose={() => setShowCaseLimitDialog(false)}
        currentCount={caseLimitData.currentCount}
        maxLimit={caseLimitData.maxLimit}
        planName={caseLimitData.planName}
      />
      </div>
    </div>
  );
}

const InfoRow = ({ label, value }) => (
  <div className="flex py-1">
    <span className="w-44 text-sm text-slate-600 shrink-0">{label}:</span>
    <span className="font-bold flex-1">{value || "N/A"}</span>
  </div>
);

export default DistrictCourtCaseDetails;
